/**
 * Interceptor global de autenticação para o dashboard principal (rotas /dashboard).
 *
 * Diferença para `wlGlobalInterceptor`:
 *  - wlGlobalInterceptor: só atua em /wl/* (White Label sub-domain).
 *  - este interceptor: atua em TODAS as chamadas /api/* feitas a partir
 *    de qualquer rota, lendo o token salvo em `localStorage.auth_token`
 *    (gravado pelo `LoginPage.jsx`).
 *
 * O backend usa o header `Authorization: Bearer {token}` para identificar
 * o usuário e aplicar filtros de escopo (representantes só veem itens deles).
 */
import axios from 'axios';

const FLAG = '__APP_AUTH_INTERCEPTOR__';

const getAuthToken = () => {
  try {
    return (
      localStorage.getItem('auth_token')
      || localStorage.getItem('token')
      || null
    );
  } catch (_e) {
    return null;
  }
};

const isApiUrl = (url) => {
  if (!url) return false;
  return url.includes('/api/') || url.startsWith('/api/');
};

/**
 * Determina se estamos em uma rota White Label (/wl/{slug}/...).
 * Quando true, este interceptor NÃO atua: o WhiteLabelLayout já tem seu
 * próprio interceptor com `wl_token` específico do tenant. Atuar aqui
 * causaria conflito de tokens e disparo de CORS preflight quebrado em
 * produção.
 */
const isWhiteLabelRoute = () => {
  try {
    return typeof window !== 'undefined' && window.location.pathname.startsWith('/wl/');
  } catch (_e) {
    return false;
  }
};

export const installAppAuthInterceptor = () => {
  if (typeof window === 'undefined') return;
  if (window[FLAG]) return;
  window[FLAG] = true;

  // ---------- AXIOS ----------
  axios.interceptors.request.use((config) => {
    try {
      // WL tem seu próprio interceptor — não interferir
      if (isWhiteLabelRoute()) return config;

      const url = config.url || '';
      if (!isApiUrl(url)) return config;

      const token = getAuthToken();
      if (!token) return config;

      config.headers = config.headers || {};
      // Não sobrescreve se outro interceptor (wl) já tiver setado
      if (!config.headers.Authorization && !config.headers.authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (e) {
      console.warn('[appAuthInterceptor axios] erro:', e);
    }
    return config;
  });

  // ---------- FETCH ----------
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    try {
      // WL tem seu próprio interceptor — não interferir
      if (isWhiteLabelRoute()) return originalFetch(input, init);

      const originalUrl = typeof input === 'string' ? input : (input?.url || '');
      if (!isApiUrl(originalUrl)) return originalFetch(input, init);

      const token = getAuthToken();
      if (!token) return originalFetch(input, init);

      const newInit = { ...init, headers: { ...(init.headers || {}) } };
      if (!newInit.headers.Authorization && !newInit.headers.authorization) {
        newInit.headers.Authorization = `Bearer ${token}`;
      }
      return originalFetch(input, newInit);
    } catch (e) {
      console.warn('[appAuthInterceptor fetch] erro:', e);
      return originalFetch(input, init);
    }
  };

  console.log('[appAuthInterceptor] instalado.');
};

export default installAppAuthInterceptor;
